import React from 'react';
import { Link } from 'react-router-dom';

import { gold } from '@ant-design/colors';
import { ExclamationCircleTwoTone as AntdWarningIcon } from '@ant-design/icons';
import {
  Badge as AntdBadge,
  Divider as AntdDivider,
  Tooltip as AntdTooltip,
} from 'antd';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  ComputedSummaryResponseDtoListActionsRequisesEnum,
  DossierSummaryResponseDto,
} from 'lib_api/lib/api/gen';

import './ActionsRequises.less';

const TEXT_CLICK_HERE_BLOCAGE_VENTE =
  'Cliquer ici pour contrôler le blocage de la vente';
const TEXT_CLICK_HERE_BLOCAGE_DOSSIER =
  'Cliquer ici pour accéder à la fiche véhicule';

function buildActionsRequisesTooltipTitle(
  actionsRequises: ComputedSummaryResponseDtoListActionsRequisesEnum[],
): React.ReactNode {
  return (
    // antd tooltip's overlay style does not seem to work with text
    // https://github.com/ant-design/ant-design/issues/23002
    <div className="actionsRequisesTooltipTitle">
      {actionsRequises
        .map(actionRequise => {
          switch (actionRequise) {
            case ComputedSummaryResponseDtoListActionsRequisesEnum.SAISIE_NOTIFICATION_MANUELLE:
              return buildAction(
                'Cliquer ici pour éditer la notification.',
                "Penser à renseigner la date d'envoi de la notification.",
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.SAISIE_VHU:
              return buildAction(
                'Le véhicule est à détruire.',
                "Cliquer ici pour renseigner le centre VHU puis télécharger le bon d'enlèvement.",
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.MODIFIER_COORDONNEES_PROPRIETAIRE:
              return buildAction(
                'Les coordonnées du propriétaire ne sont pas suffisantes pour le notifier.',
                'Cliquer ici pour compléter les coordonnées.',
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RENSEIGNER_STATUT_RETOUR_NOTIFICATION_MANUELLE:
              return buildAction(
                'La notification a été envoyée il y a 20 jours.',
                'Cliquer ici pour renseigner la date et le statut de retour de notification.',
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.PROLONGER_MEF:
              return buildAction(
                'Si une prolongation de mise en fourrière administrative a été prescrite par le juge,',
                'cliquer ici pour la renseigner.',
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.ENREGISTRER_ENTREE:
              return buildAction(
                'Le dossier doit être complété pour finaliser l’enregistrement du véhicule.',
                'Cliquer ici pour remplir le dossier.',
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLER_DONNEES_VEHICULE:
              return buildAction(
                'Un doute a été émis sur la fiabilité des données du véhicule.',
                'Cliquer ici pour les contrôler.',
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLE_DONNEES_ERREUR_IDENTIFICATION_VEHICULE:
              return buildAction(
                "Vente bloquée suite à une potentielle erreur d'identification du véhicule",
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_VOL:
              return buildAction(
                'Vente bloquée, le véhicule est identifié comme volé',
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_IMMO_PJ:
              return buildAction(
                // Use non-breaking space to prevent ugly line break
                'Vente bloquée pour mention "Immo\u00A0PJ"',
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_OPPOSITION:
              return buildAction(
                'Vente bloquée pour mention "Opposition"',
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_DOUBLON:
              return buildAction(
                'Vente bloquée pour doublon à confirmer',
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_MANQUANT:
              return buildAction(
                'Vente bloquée, le véhicule est considéré comme manquant',
                TEXT_CLICK_HERE_BLOCAGE_VENTE,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLE_BLOCAGE_ARGOS:
              return buildAction(
                "Le délai d'attente de retour d'ARGOS est anormalement long",
                TEXT_CLICK_HERE_BLOCAGE_DOSSIER,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLE_BLOCAGE_ANTAI:
              return buildAction(
                "Le délai d'attente de la remise en poste est anormalement long",
                TEXT_CLICK_HERE_BLOCAGE_DOSSIER,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLE_BLOCAGE_DNID:
              return buildAction(
                "Le délai d'attente de la vente/refus de ce véhicule par la DNID est anormalement long",
                TEXT_CLICK_HERE_BLOCAGE_DOSSIER,
              );
            case ComputedSummaryResponseDtoListActionsRequisesEnum.CONFIRMER_VOL_VVNR:
              return buildAction(
                'Ce véhicule est recherché par un assureur suite à un vol déclaré',
                "Cliquez ici pour confirmer la découverte d'un véhicule volé",
              );
            default:
              console.warn(
                'Unhandled action requise in dashboard tooltip',
                actionRequise,
              );
              return null;
          }
        })
        .reduce((first, second) => [
          first,
          <AntdDivider key={Math.random()} style={{ borderColor: 'gray' }} />,
          second,
        ])}
    </div>
  );
}

function buildAction(
  libelle: string,
  libelleClickHere: string,
): React.ReactNode {
  return (
    <>
      <p>{libelle}</p>
      <p className="actionsRequisesClickHere">{libelleClickHere}</p>
    </>
  );
}

export interface ActionsRequisesLinkState {
  openCoordonneesProprietaire?: boolean;
  openSaisieVhu?: boolean;
  openSuiviNotification?: boolean;
  openProlongationMef?: boolean;
  openEntreeVehicule?: boolean;
  openControlerDonnees?: boolean;
  openControleBlocageVenteManquant?: boolean;
  openControleBlocageVenteVol?: boolean;
  openControleBlocageVenteImmoPJ?: boolean;
  openControleBlocageVenteOpposition?: boolean;
  openControleBlocageVenteDoublon?: boolean;
  openControleBlocageVenteErreurIdentification?: boolean;
  openConfirmationVolVvnr?: boolean;
  openValidationActionsVvnrHorsSif?: boolean;
  openDeterminerIdentifiabiliteProprietaire?: boolean;
}

function buildActionsRequisesLinkState(
  actionsRequises: ComputedSummaryResponseDtoListActionsRequisesEnum[],
): ActionsRequisesLinkState {
  if (actionsRequises.length > 1) {
    // do not open any modal by default if there are more than one to be opened
    return {};
  }

  return {
    openCoordonneesProprietaire:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.MODIFIER_COORDONNEES_PROPRIETAIRE,
    openSaisieVhu:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.SAISIE_VHU,
    openSuiviNotification:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RENSEIGNER_STATUT_RETOUR_NOTIFICATION_MANUELLE,
    openProlongationMef:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.PROLONGER_MEF,
    openEntreeVehicule:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.ENREGISTRER_ENTREE,
    openControlerDonnees:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLER_DONNEES_VEHICULE,
    openControleBlocageVenteManquant:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_MANQUANT,
    openControleBlocageVenteVol:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_VOL,
    openControleBlocageVenteImmoPJ:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_IMMO_PJ,
    openControleBlocageVenteOpposition:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_OPPOSITION,
    openControleBlocageVenteDoublon:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.RELANCE_VENTE_DOUBLON,
    openControleBlocageVenteErreurIdentification:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.CONTROLE_DONNEES_ERREUR_IDENTIFICATION_VEHICULE,
    openConfirmationVolVvnr:
      actionsRequises[0] ===
      ComputedSummaryResponseDtoListActionsRequisesEnum.CONFIRMER_VOL_VVNR,
  };
}

export function buildActionsRequisesContent(
  url: string,
  data: DossierSummaryResponseDto,
  profileActionsRequises: ActionsRequisesDtoActionsRequisesEnum[] | undefined,
): React.ReactNode {
  const antdWarningColor = gold.primary;
  const actionsRequises = data.computed?.listActionsRequises?.filter(
    actionRequise =>
      profileActionsRequises?.includes(
        ActionsRequisesDtoActionsRequisesEnum[actionRequise],
      ),
  );

  if (!!actionsRequises && actionsRequises.length > 0) {
    return (
      <Link
        to={`${url}/dossier/${data.id}`}
        state={buildActionsRequisesLinkState(actionsRequises)}
        className="actionsRequisesLink"
      >
        <AntdTooltip
          title={buildActionsRequisesTooltipTitle(actionsRequises)}
          overlayStyle={{ cursor: 'pointer' }}
        >
          <AntdBadge
            count={
              // do not display the badge if only one action is required
              actionsRequises.length > 1 ? actionsRequises.length : 0
            }
            size={'small'}
          >
            <AntdWarningIcon
              twoToneColor={antdWarningColor}
              className="actionsRequisesIcon"
            />
          </AntdBadge>
        </AntdTooltip>
      </Link>
    );
  }
  return null;
}
